import _debounce from 'lodash/debounce';
import _clone from 'lodash/clone';
import _get from 'lodash/get';
import _uniqId from 'lodash/uniqueId';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import RouteOptimizationLocationDrawer from 'components/v2/route-optimization-location-drawer/route-optimization-location-drawer';
import OrderSummaryWidget from 'components/v2/order-summary-widget/order-summary-widget';
import BackButton from 'components/v2/back-button/back-button';
import {
  createOrderActionV2,
  updateOrderAction,
  fetchServiceTypesAction,
  updateBulkOrderAction,
  updateOrderShallowAction,
  updateBulkOrderShallowAction,
} from 'actions/booking-actions';
import {
  toggleLocationDrawerAction,
  pushNotificationAction,
  showFailedPaymentsRetryModalAction,
} from 'actions/app-actions';
import {
  userInfoSelector,
  isCashPaymentDisabledSelector,
  isCashlessPaymentDisabledSelector,
} from 'selectors/app-selector';
import { paymentMethodSelector } from 'selectors/booking-selectors';
import {
  toPlaceOrder,
  setExtraFields,
  getSelectedServiceType,
  moveExtraFields,
} from 'helper/order';
import { getCompanyID } from 'helper/user';
import { trackClick, event, cat, custom } from 'common/utils/instrumentation';
import {
  scribeTrackEvent,
  getCommonFacet,
  scribeTrackOrderPageCreateBooking,
  SCRIBE_STATE_NAMES,
  SCRIBE_DELIVERY_DETAILS_EVENTS,
  SCRIBE_EVENT_PARAMS,
} from 'applications/scribe/instrumentations/track';
import { MODE } from 'views/v2/home/constants';
import { pushGABookDeliveriesEvent } from 'helper/ga-helper';

import BookOrderForm from './book-order-form';
import css from './single-order-page.less';

class SingleOrderPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      locationDrawerID: _uniqId('drawer'),
      onConfirmUpdateLocation: () => {},
    };
  }

  componentDidMount() {
    // useful for Book Again action
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const { order, userInfo } = this.props;
    if (order && userInfo) {
      scribeTrackEvent({
        stateName: SCRIBE_STATE_NAMES.EXPRESS_DELIVERY_DETAILS_WEB,
        eventName: SCRIBE_DELIVERY_DETAILS_EVENTS.DEFAULT,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.PICKUP_POI_ID]: _get(order, 'senderLocation.id'),
          [SCRIBE_EVENT_PARAMS.DROPOFF_POI_ID]: _get(
            order,
            'recipientLocations',
            [],
          ).map(loc => loc.id),
          [SCRIBE_EVENT_PARAMS.SERVICE_TYPE]: _get(order, 'SERVICE_TYPE.value'),
          [SCRIBE_EVENT_PARAMS.TAXI_TYPE]: _get(order, 'SERVICE_TYPE.value'),
          [SCRIBE_EVENT_PARAMS.FARE]: _get(order, 'quote.forRender.amount'),
          [SCRIBE_EVENT_PARAMS.BOOKING_MODE]: MODE.SINGLE_BOOK,
        },
      });
    }
  }

  onClickPickupLocation = () => {
    const { order, toggleLocationDrawer } = this.props;
    this.setState({
      locationDrawerID: _uniqId('drawer'),
      allowMultipleAddresses: false,
      onConfirmUpdateLocation: this.onConfirmPickupLocation,
    });
    toggleLocationDrawer([order.senderLocation], true);
  };

  onUpdateDropoffLocations = (locations, oldIndexes) => {
    const { order, updateOrder } = this.props;
    let newOrder = {
      ...order,
      recipientLocations: locations,
    };
    newOrder = moveExtraFields(newOrder, oldIndexes);
    setExtraFields(newOrder);
    updateOrder(newOrder);
  };

  onEditAllDropoffLocations = () => {
    const { order, toggleLocationDrawer } = this.props;
    this.setState({
      locationDrawerID: _uniqId('drawer'),
      allowMultipleAddresses: true,
      onConfirmUpdateLocation: this.onUpdateDropoffLocations,
    });
    toggleLocationDrawer(order.recipientLocations, false, true);
  };

  onClickDropoffLocation = index => () => {
    const { order, toggleLocationDrawer } = this.props;
    this.setState({
      onConfirmUpdateLocation: this.onConfirmDropoffLocation(index),
    });
    toggleLocationDrawer([order.recipientLocations[index]]);
  };

  onConfirmPickupLocation = locations => {
    const { order, updateOrder, userInfo } = this.props;
    const companyID = getCompanyID(userInfo);
    trackClick(
      event.DELIVERY_DETAILS_CLICKS,
      cat.GE_DELIVERY_DETAILS,
      'PICKUP_ADDRESS',
      {
        [custom.COMPANY_ID]: companyID,
        PICKUP_POI: _get(order, 'senderLocation.id'),
        DELIVERY_NUMBER: 0,
      },
    );
    scribeTrackEvent({
      stateName: SCRIBE_STATE_NAMES.EXPRESS_DELIVERY_DETAILS_WEB,
      eventName: SCRIBE_DELIVERY_DETAILS_EVENTS.PICKUP_ADDRESS,
      facets: {
        ...getCommonFacet(userInfo),
        [SCRIBE_EVENT_PARAMS.PICKUP_POI_ID]: _get(order, 'senderLocation.id'),
        [SCRIBE_EVENT_PARAMS.DELIVERY_NUMBER]: 0,
      },
    });

    const newOrder = {
      ...order,
      senderLocation: locations[0],
    };
    updateOrder(newOrder);
  };

  onConfirmDropoffLocation = index => locations => {
    const { order, updateOrder, userInfo } = this.props;
    const companyID = getCompanyID(userInfo);
    trackClick(
      event.DELIVERY_DETAILS_CLICKS,
      cat.GE_DELIVERY_DETAILS,
      'DROPOFF_ADDRESS',
      {
        [custom.COMPANY_ID]: companyID,
        DROPOFF_POI: _get(order, `recipientLocations[${index}].id`),
        DELIVERY_NUMBER: 0,
      },
    );
    scribeTrackEvent({
      stateName: SCRIBE_STATE_NAMES.EXPRESS_DELIVERY_DETAILS_WEB,
      eventName: SCRIBE_DELIVERY_DETAILS_EVENTS.DROPOFF_ADDRESS,
      facets: {
        ...getCommonFacet(userInfo),
        [SCRIBE_EVENT_PARAMS.DROPOFF_POI_ID]: _get(
          order,
          'recipientLocations',
        ).map(loc => loc.id),
        [SCRIBE_EVENT_PARAMS.DELIVERY_NUMBER]: 0,
      },
    });

    const recipientLocations = _clone(order.recipientLocations);
    recipientLocations.splice(index, 1, locations[0]);
    const newOrder = {
      ...order,
      recipientLocations,
    };
    setExtraFields(newOrder);
    updateOrder(newOrder);
  };

  onDeleteRecipient = index => () => {
    const { order, updateOrder } = this.props;
    const recipientLocations = _clone(order.recipientLocations);
    recipientLocations[index] = { deleted: true };
    const newOrder = {
      ...order,
      recipientLocations,
    };
    updateOrder(newOrder);
  };

  onClickPlaceOrder = _debounce(() => {
    const {
      order,
      userInfo,
      payments,
      createOrder,
      onOrderCreateComplete,
    } = this.props;
    const orderToPlace = toPlaceOrder([order], userInfo, payments);
    scribeTrackOrderPageCreateBooking(order, userInfo, MODE.SINGLE_BOOK);
    createOrder(orderToPlace, () => {
      onOrderCreateComplete();
    });
    pushGABookDeliveriesEvent(
      userInfo,
      MODE.SINGLE_BOOK,
      _get(order, 'quote.forRender.amount'),
      getSelectedServiceType(order).name,
      1,
    );
  }, 500);

  buildFormID = () => {
    const { senderLocation, recipientLocations = [] } = this.props.order;
    return (
      [senderLocation, ...recipientLocations]
        .map(loc => _get(loc, 'id'))
        .join('-') || 'empty-form'
    );
  };

  renderBookOrderForms = () => {
    const {
      mode,
      order,
      payments,
      updateOrder,
      updateOrderShallow,
      updateBulkOrder,
      updateBulkOrderShallow,
      userInfo,
      isCashPaymentDisabled,
      isCashlessPaymentDisabled,
      pushNotification,
      showFailedPaymentsRetryModal,
    } = this.props;

    return (
      <BookOrderForm
        key={this.buildFormID()}
        enableRouteOptimization
        index={null}
        mode={mode}
        order={order}
        payments={payments}
        updateOrder={updateOrder}
        updateOrderShallow={updateOrderShallow}
        updateBulkOrder={updateBulkOrder}
        updateBulkOrderShallow={updateBulkOrderShallow}
        onClickPickupLocation={this.onClickPickupLocation}
        onEditAllDropoffLocations={this.onEditAllDropoffLocations}
        onDeleteRecipient={this.onDeleteRecipient}
        userInfo={userInfo}
        isCashPaymentDisabled={isCashPaymentDisabled}
        isCashlessPaymentDisabled={isCashlessPaymentDisabled}
        pushNotification={pushNotification}
        onRetryFailedPayments={showFailedPaymentsRetryModal}
      />
    );
  };

  render() {
    const {
      onConfirmUpdateLocation,
      locationDrawerID,
      allowMultipleAddresses,
    } = this.state;
    const { onClickBack, order, userInfo } = this.props;
    const selectedServiceType = getSelectedServiceType(order);
    const multiStopMaxStopCount = _get(selectedServiceType, 'maxMSDStops', 1);

    return (
      <div>
        <BackButton onClick={onClickBack} />
        <h4 className={css.pageHeader}>
          {I18n.t('v2.homepage.manualOrder.heading.singleBook')}
        </h4>
        <div className={css.orderPanel}>
          <div className={css.orderForms}>{this.renderBookOrderForms()}</div>
          <OrderSummaryWidget
            isForBulk={false}
            order={order}
            onClickPlaceOrder={this.onClickPlaceOrder}
            userInfo={userInfo}
          />
        </div>
        <RouteOptimizationLocationDrawer
          key={locationDrawerID}
          order={order}
          onUpdate={onConfirmUpdateLocation}
          allowMultipleAddresses={allowMultipleAddresses}
          maxMSDStops={multiStopMaxStopCount}
        />
      </div>
    );
  }
}

SingleOrderPage.propTypes = {
  mode: PropTypes.string.isRequired,
  order: PropTypes.shape({
    recipientLocations: PropTypes.arrayOf(PropTypes.shape({})),
    senderLocation: PropTypes.shape({}),
  }),
  payments: PropTypes.arrayOf(PropTypes.shape({})),
  userInfo: PropTypes.shape({}).isRequired,
  onClickBack: PropTypes.func.isRequired,
  updateOrder: PropTypes.func.isRequired,
  updateOrderShallow: PropTypes.func.isRequired,
  updateBulkOrder: PropTypes.func.isRequired,
  updateBulkOrderShallow: PropTypes.func.isRequired,
  toggleLocationDrawer: PropTypes.func.isRequired,
  createOrder: PropTypes.func.isRequired,
  onOrderCreateComplete: PropTypes.func.isRequired,
  isCashPaymentDisabled: PropTypes.bool,
  isCashlessPaymentDisabled: PropTypes.bool.isRequired,
  pushNotification: PropTypes.func.isRequired,
  showFailedPaymentsRetryModal: PropTypes.func.isRequired,
};

SingleOrderPage.defaultProps = {
  order: {},
  payments: [],
  isCashPaymentDisabled: false,
};

const mapStateToProps = state => ({
  order: state.booking.order,
  orders: state.booking.orders,
  payments: paymentMethodSelector(state),
  userInfo: userInfoSelector(state),
  isCashPaymentDisabled: isCashPaymentDisabledSelector(state),
  isCashlessPaymentDisabled: isCashlessPaymentDisabledSelector(state),
});

const mapDispatchToProps = {
  createOrder: createOrderActionV2,
  updateOrder: updateOrderAction,
  updateOrderShallow: updateOrderShallowAction,
  updateBulkOrder: updateBulkOrderAction,
  updateBulkOrderShallow: updateBulkOrderShallowAction,
  fetchServiceTypes: fetchServiceTypesAction,
  toggleLocationDrawer: toggleLocationDrawerAction,
  pushNotification: pushNotificationAction,
  showFailedPaymentsRetryModal: showFailedPaymentsRetryModalAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(SingleOrderPage);
